/// <amd-module name="Repos/Views/Settings/Options/Components/Options" />
import React = require("react");
import { AdvSecEnablementStatus, GitRepository } from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions } from "Repos/Common/Permissions";
import * as Resources from "Repos/Views/Settings/Options/Resources";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IOption } from "../Contracts/Contracts";
import { IOptionsViewService } from "../Services/OptionsViewService";
import { OptionToggle } from "./OptionToggle";
import { SuggestionCard } from "./SuggestionCard";


interface IOptionsProps {
    defaultBranch?: string;
    projectId: string;
    repository?: GitRepository;
    isTfvc?: boolean;
}

type OptionsCard = { title: string; options: IOption[]; disabled: boolean; };

export class Options extends VssComponent<IOptionsProps> {
    private readonly optionsData = new ObservableArray([] as IOption[]);
    private readonly optionsSvc: IOptionsViewService;
    private readonly repositoryId: string | undefined;
    private readonly contributionService: IVssContributionService;

    // Start all toggles off disabled until the options service proves otherwise
    private readonly editPoliciesDisabled = new ObservableValue(true);
    private readonly disabledRepositoryDisabled = new ObservableValue(true);
    private suggestionFieldInitial: string;
    private suggestionOption: IOption;
    private advSecEnablementData = new ObservableArray([] as AdvSecEnablementStatus[]); // wrapping data as array allows us to use as Observed prop

    public constructor(props: IOptionsProps, context: IVssComponentContext) {
        super(props, context);
        this.repositoryId = this.props.repository?.id;
        this.optionsSvc = context.pageContext.getService<IOptionsViewService>("OptionsViewService");
        this.contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        
        Promise.all([
            this.optionsSvc.getRepositoryOptions(this.props.projectId, this.repositoryId, this.props.isTfvc),
            this.optionsSvc.checkPermissionsAsync(GitRepositoryPermissions.EditPolicies, props.projectId, this.repositoryId),
            this.optionsSvc.checkPermissionsAsync(GitRepositoryPermissions.DeleteRepository, props.projectId, this.repositoryId),
            this.contributionService.getDataAsync("ms.vss-advsec.advanced-security-enablement-data-provider", {
                givenProjectId: this.props.projectId,
                givenRepoId: this.repositoryId
            }, true)
        ]).then(data => {
            this.advSecEnablementData.value = [data[3] as AdvSecEnablementStatus];
            this.optionsData.value = data[0];
            this.editPoliciesDisabled.value = !data[1];
            this.disabledRepositoryDisabled.value = !data[2];
        });
    }

    public render = (): JSX.Element => {
        return (
            <div className="flex-column rhythm-vertical-20">
                <Observer
                    optionsData={this.optionsData}
                    editPoliciesDisabled={this.editPoliciesDisabled}
                    disabledRepositoryDisabled={this.disabledRepositoryDisabled}
                    advSecEnablementData={this.advSecEnablementData}>
                    {() => {
                        const title = this.props.isTfvc
                            ? Resources.OptionsTitleTfvc
                            : this.repositoryId
                                ? Resources.OptionsTitle
                                : Resources.OptionsTitleAllRepos;

                        if (this.optionsData.length === 0) {
                            return (
                                <div className="flex-grow justify-center flex-column padding-16">{renderLoading()}</div>
                            );
                        }

                        const repositorySettingsCard: OptionsCard = { title, options: [], disabled: this.editPoliciesDisabled.value };
                        const disableRepositoryCard: OptionsCard = { title: Resources.DisableRepo, options: [], disabled: this.disabledRepositoryDisabled.value };

                        // Designate which toggles should go into which standalone card, the rest goes into the default
                        const standaloneCardOptions: { [key: string]: OptionsCard } = {
                            IsDisabled: disableRepositoryCard,
                        };

                        // Assign options to the default card unless their `key` matches a set of keys of a standalone card
                        for (const option of this.optionsData.value) {
                            if (option.key == "Suggestion") {
                                this.suggestionOption = option;
                                if (option.textValue !== undefined) {
                                    this.suggestionFieldInitial = option.textValue;
                                }
                            } else {
                                const card = standaloneCardOptions[option.key] ?? repositorySettingsCard;
                                card.options.push(option);
                            }
                        }

                        const cards = [repositorySettingsCard, disableRepositoryCard];
                        return (
                            <div className="policies-container">
                                <RegionComponent
                                    componentRegion="ms.vss-code-web.advanced-security-settings-card-extension"
                                    childComponentProperties={{
                                        projectId: this.props.projectId,
                                        repositoryId: this.repositoryId,
                                        pageContext: this.context.pageContext,
                                        enablementStatus: this.advSecEnablementData.value[0]
                                    }}
                                />
                                {cards.filter(card => card.options.length > 0).map(card => (
                                    <Card key={card.title} className="flex-grow policies" titleProps={{ text: card.title }} contentProps={{ contentPadding: false }}>
                                        <div className="flex-column" key={card.title}>
                                            {card.options.map(option => {
                                                const repositoryId = this.repositoryId || "global";
                                                const key = `${this.props.projectId}/${repositoryId}/${option.key}`;

                                                // Check if option is not linked to a parent key (i.e. a child)
                                                if (!option.parentOptionKey) {
                                                    let children = card.options.filter((childOption) => childOption.parentOptionKey === option.key)
                                                    if (children.length > 0) {
                                                        return (
                                                            <>
                                                                <OptionToggle
                                                                    key={key}
                                                                    option={option}
                                                                    onToggle={this.onToggle}
                                                                    disabled={card.disabled}
                                                                />
                                                                <div className="option-child">
                                                                    {...children.map(child => {
                                                                        const childKey = `${this.props.projectId}/${repositoryId}/${child.key}`;
                                                                        return (
                                                                            <OptionToggle
                                                                                key={childKey}
                                                                                option={child}
                                                                                onToggle={this.onToggle}
                                                                                disabled={card.disabled || option.value}
                                                                            />
                                                                        )
                                                                    })}
                                                                </div>
                                                            </>
                                                        );
                                                    }
                                                    else {
                                                        return (
                                                            <OptionToggle
                                                                key={key}
                                                                option={option}
                                                                onToggle={this.onToggle}
                                                                disabled={card.disabled}
                                                            />
                                                        )
                                                    }
                                                }
                                            })}
                                        </div>
                                    </Card>
                                ))}
                                {isFeatureFlagEnabled(this.context.pageContext, "SourceControl.SuggestBranchName", false) ?
                                    <SuggestionCard
                                        option={this.suggestionOption}
                                        suggestionFieldInitial={this.suggestionFieldInitial}
                                        projectId={this.props.projectId}
                                        repositoryId={this.repositoryId}
                                        optionsSvc={this.optionsSvc}
                                    /> : null}
                            </div>
                        );
                    }}
                </Observer>

                {this.props.repository && (
                    <RegionComponent
                        componentRegion="ms.vss-code-web.settings-options-component.search-extension"
                        childComponentProperties={{
                            defaultBranch: this.props.defaultBranch,
                            projectId: this.props.projectId,
                            repository: this.props.repository
                        }}
                    />
                )}
            </div>
        );
    };

    private onToggle = async (option: IOption, newText: string) => {
        const { validationError } = await this.optionsSvc
            .toggleRepositoryOption(option, this.props.projectId, this.repositoryId, newText);
        this.optionsData.value = await this.optionsSvc.getRepositoryOptions(this.props.projectId, this.repositoryId, this.props.isTfvc);
        return validationError;
    };
}

function renderLoading() {
    return <Spinner size={SpinnerSize.large} />;
}

Components.add("ms.vss-code-web.settings-options-component", Options);